import { Platform } from 'react-native'
import * as Updates from 'expo-updates'
import appJson from '../app.json'

/**
 * ATL-531 — the STATIC build provenance every PostHog event should carry.
 *
 * These values identify the bundle that emitted an event and nothing about the person emitting it,
 * so they are registered as PostHog SUPER PROPERTIES: set once, attached to everything after.
 *
 * They live here, rather than inline in the registrar that first sets them, because super
 * properties are PERSISTED STATE and `postHog.reset()` wipes them (posthog-core `reset()` nulls
 * every persisted key, `Props` included). Identity now resets on sign-out (ASO-AN-001), and the
 * registrar's effect is keyed on the PostHog client — which does not change across a logout — so
 * without a second caller able to build this exact payload, every event for the rest of the
 * process would lose `update_id`/`js_version`. Those are the fields the read-cutover
 * `ota-coverage` gate reads; losing them makes a device permanently `unverifiable`.
 *
 * NOTE: the NATIVE binary version is auto-captured by PostHog as $app_version/$app_build;
 * js_version is the bundled app.json version — the post-OTA JS version, present on web too.
 * updates_enabled/update_is_embedded disambiguate update_id:null (web vs dev/disabled vs
 * embedded-launch-no-OTA-yet — the last is common and legitimate).
 *
 * @returns {Record<string, unknown>}
 */
export function buildProvenanceProperties() {
  return {
    update_id: Platform.OS === 'web' ? null : Updates.updateId,
    runtime_version: Platform.OS === 'web' ? null : Updates.runtimeVersion,
    update_channel: Platform.OS === 'web' ? null : Updates.channel,
    updates_enabled: Platform.OS === 'web' ? null : Updates.isEnabled,
    update_is_embedded: Platform.OS === 'web' ? null : Updates.isEmbeddedLaunch,
    js_version: appJson.expo.version,
    platform: Platform.OS,
    // ATL-895: web has no OTA, so update_id is null on every web event and the read-cutover
    // gate can only ever call a web client `unverifiable` — a permanent NO-GO for any org whose
    // staff use the browser (7 of the 22 busiest trading orgs, measured 2026-08-13). The web
    // bundle therefore carries the instant it was BUILT, stamped by scripts/submit-web.sh at
    // export time. It is baked into the bundle, so a tab left open since before a deploy keeps
    // reporting the OLD instant — which is exactly the stale-tab case the gate must catch.
    // Absent (a bundle predating this) stays null and stays unverifiable: fail-closed.
    web_build_at: Platform.OS === 'web' ? process.env.EXPO_PUBLIC_WEB_BUILD_AT ?? null : null,
  }
}
